import { ILegalEntity } from '@cemex-core/cmx-api';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { createSelector } from '@ngrx/store';
import { IBusinessLineState } from '@ordersnproducts/modules/order-builder/modules/business-line-selection/reducers/business-line-page.reducer';
import { getBusinessLinePage } from '@ordersnproducts/modules/order-builder/reducers';
import { getNewModifyOrderState } from '../../../reducers';
import { RecipeModifyFormValues } from '../models/form-values';
import { IJobsiteState } from '../reducers/jobsite-reducer/jobsite.reducer';
import { IProductsState } from '../reducers/products-reducer/products.reducer';
import * as fromUnits from '../reducers/unit-reducer/units.reducer';
import * as fromReceivingPersons from '../reducers/receiving-persons.reducer';
import * as fromContracts from '../reducers/contracts.reducer';
import * as fromAdditionalServices from '../reducers/additional-services.reducer';
import { _safe } from '../../../../../modules/shared/utils/safe-get.method';

/**
 * Exports location specification state
 */
export const getModifyOnlineOrderState = createSelector(getNewModifyOrderState, state => {
  return state.modifyOnlineOrder;
});

export const getModifyOnlineOrderFormsState = createSelector(getNewModifyOrderState, state => {
  return state.modifyOnlineOrder.modifyFormState;
});

export const getModifyOnlineOrderContactPersonFormConfig = createSelector(getNewModifyOrderState, state => {
  return state.modifyOnlineOrder.modifyFormState.contactPersonFormConfig;
});

export const getRecipeModifyOnlineOrders = createSelector(getModifyOnlineOrderState, state => {
  return state.modifyFormState.forms;
});

export const getProcessingFormsState = createSelector(getModifyOnlineOrderState, state => {
  return state.modifyFormState.isProcessingForms;
});

export const getStopProcessingFormState = createSelector(getModifyOnlineOrderState, state => {
  return state.modifyFormState.stopProcessingForm;
});

export const getResetTimeTo = createSelector(getModifyOnlineOrderState, state => {
  return state.modifyFormState.resetTimeTo;
});

export const getModifyOnlineOrderUnits = createSelector(getModifyOnlineOrderState, state => {
  return state.units;
});

export const getJobsiteState = createSelector(getModifyOnlineOrderState, state => {
  return state.jobsite;
});

export const getSalesAreaState = createSelector(getModifyOnlineOrderState, state => {
  return state.jobsite.selectedSalesArea;
});

export const getTopProductState = createSelector(getModifyOnlineOrderState, state => {
  return state.products;
});

export const getNextWorkingDay = createSelector(getModifyOnlineOrderState, state => {
  return state.nextWorkingDay;
});

export const getReceivingPerson = createSelector(getModifyOnlineOrderState, state => {
  return state.receivingPersons;
});

export const getHolidays = createSelector(getModifyOnlineOrderState, state => {
  return state.disabledDates;
});

export const getModifyUnitsState = createSelector(getModifyOnlineOrderState, state => state && state.units);

export const getModifyUnitsArray = createSelector(getModifyUnitsState, fromUnits.getUnits);

export const getModifyContractState = createSelector(getModifyOnlineOrderState, state => state && state.contracts);

export const getModifyContractArray = createSelector(getModifyContractState, fromContracts.getContracts);

const getReceivingPersonsState = createSelector(getModifyOnlineOrderState, state => state && state.receivingPersons);

export const getContactPersonsArray = createSelector(getReceivingPersonsState, fromReceivingPersons.getContactPersons);

export const getPurchaseOrderValid = createSelector(getModifyOnlineOrderState, state => {
  return state.asyncValidationModify;
});

export const getSelectedReceivingPersonId = createSelector(
  getReceivingPersonsState,
  fromReceivingPersons.getSelectedContactPersonId,
);

export const getHolidaysAndNextWorkingDay = createSelector(
  getNextWorkingDay,
  getHolidays,
  (nextWorkingDay, holidays) => {
    return {
      nextWorkingDay: nextWorkingDay.nextWorkingDay,
      blackout: nextWorkingDay.blackout,
      holidays: holidays.holidays,
    };
  },
);

export const getFormResetTimeTo = createSelector(
  getRecipeModifyOnlineOrders,
  getResetTimeTo,
  (forms, resetTimeTo) => {
    return {
      forms,
      resetTimeTo,
    };
  },
);

export const getPurchaseOrderValidationStateByFormId = formId =>
  createSelector(getPurchaseOrderValid, state =>
    state.forms.find(x => x.id === formId) ? state.forms.find(x => x.id === formId).purchaseOrderValidating : null,
  );

export const getPurchaseOrderValidStateByFormId = formId =>
  createSelector(getPurchaseOrderValid, state => {
    return state.forms.find(x => x.id === formId) ? state.forms.find(x => x.id === formId).purchaseOrderValid : null;
  });

export const getStateForLoadProductsConfig = index =>
  createSelector(
    getJobsiteState,
    getBusinessLinePage,
    getTopProductState,
    getLegalEntity,
    getRecipeModifyOnlineOrders,
    getReceivingPerson,
    getPurchaseOrderValidationStateByFormId(index),
    getPurchaseOrderValidStateByFormId(index),
    (
      jobsite: IJobsiteState,
      businessLineState: IBusinessLineState,
      product: IProductsState,
      customer: ILegalEntity,
      previousFormState: RecipeModifyFormValues[],
      receivingPerson: any,
      purchaseOrderValidating: boolean,
      purchaseOrderValid: boolean,
    ) => {
      return {
        jobsite,
        businessLineState,
        product,
        customer,
        previousFormState,
        receivingPerson,
        purchaseOrderValidating,
        purchaseOrderValid,
      };
    },
  );

export const getStateForLoadConfigModify = createSelector(
  getLegalEntity,
  getBusinessLinePage,
  (customer: ILegalEntity, businessLineState: IBusinessLineState) => {
    return { customer, businessLineState };
  },
);

export const getSelectedJobsiteSalesArea = createSelector(getJobsiteState, state => {
  return state.selectedSalesArea;
});

export const getPurchaseOrderValidation = createSelector(getSelectedJobsiteSalesArea, selectedSalesArea => {
  if (selectedSalesArea) {
    return selectedSalesArea.purchaseOrderValidation;
  }
  return undefined;
});

export const getAdditionalServicesState = createSelector(
  getModifyOnlineOrderState,
  state => state && state.additionalServices,
);

export const getAdditionalServiceArray = createSelector(
  getAdditionalServicesState,
  fromAdditionalServices.getAdditionalServices,
);

export const getSelectedExperienceModify = createSelector(getJobsiteState, state => {
  return { experience: state.experience, selectedJobsite: state.selected };
});

export const getPurchaseOrderValidationAndExperience = createSelector(
  getPurchaseOrderValidation,
  getSelectedExperienceModify,
  (purchaseOrderMandatory: boolean, jobsiteExperience: any) => {
    return { purchaseOrderMandatory, jobsiteExperience };
  },
);

export const getSelectedModifyJobsite = createSelector(getJobsiteState, state => {
  return state.selected;
});

export const getPurchaseOrderMaskPatterns = createSelector(getJobsiteState, state => {
  return state.purchaseOrderMaskPatterns;
});

export const getStateForPurchaseOrderValidator = createSelector(
  getJobsiteState,
  getBusinessLinePage,
  getPurchaseOrderValidation,
  (jobsite: IJobsiteState, businessLineState: IBusinessLineState, purchaseOrderMandatory: boolean) => {
    return { jobsite, businessLineState, purchaseOrderMandatory };
  },
);

export const getAsyncValidatorsFormById = formId =>
  createSelector(getModifyOnlineOrderState, state => {
    return _safe(() => state.asyncValidationModify.forms.find(x => x.id === formId), null);
  });

export const getFormsStateById = formId =>
  createSelector(getModifyOnlineOrderState, state => {
    return _safe(() => state.modifyFormState.forms.find(x => x.id === formId), null);
  });

export const getFormsStateAndValidatorsById = formId =>
  createSelector(
    getFormsStateById(formId),
    getAsyncValidatorsFormById(formId),
    (formState: any, asyncValidatorsForm: any) => {
      return { formState, asyncValidatorsForm };
    },
  );

export const getStateForJobsiteLoadExperience = createSelector(
  getJobsiteState,
  getBusinessLinePage,
  getLegalEntity,
  (jobsite: IJobsiteState, businessLineState: IBusinessLineState, customer: ILegalEntity) => {
    return { jobsite, businessLineState, customer };
  },
);

export const getIsLoading = createSelector(getModifyOnlineOrderState, state => {
  return state.modifyFormState.isLoading;
});
